import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useTTS } from './useTTS';
import { registerSeHandle } from '../state/seWindow';

// Shared TTS instance — drives per-message ▶ buttons and streaming auto-read.
// Reader Mode uses a SEPARATE instance (ReaderTtsProvider) so the two channels
// have independent audio elements, queues, and state. The two cross-stop on
// start so audio never plays simultaneously, but UI/UX state is fully decoupled.
type TtsApi = ReturnType<typeof useTTS>;

const TtsContext = createContext<TtsApi | null>(null);
const ReaderTtsContext = createContext<TtsApi | null>(null);

export function TtsProvider({ children }: { children: ComponentChildren }) {
  const tts = useTTS();
  // SE seam (issue #2201): expose the REAL api on window.__vaSe when the page was loaded
  // with ?vaSe=1 — the desktop-home SEs drive the hero's speaking state through the same
  // public entry points the read-aloud buttons call. Inert in normal use (no param →
  // no seam object → registerSeHandle is a no-op).
  registerSeHandle('tts', tts);
  return <TtsContext.Provider value={tts}>{children}</TtsContext.Provider>;
}

export function useTtsShared(): TtsApi {
  const ctx = useContext(TtsContext);
  if (!ctx) throw new Error('useTtsShared() called outside <TtsProvider>');
  return ctx;
}

export function ReaderTtsProvider({ children }: { children: ComponentChildren }) {
  const tts = useTTS();
  return <ReaderTtsContext.Provider value={tts}>{children}</ReaderTtsContext.Provider>;
}

export function useReaderTts(): TtsApi {
  const ctx = useContext(ReaderTtsContext);
  if (!ctx) throw new Error('useReaderTts() called outside <ReaderTtsProvider>');
  return ctx;
}
